import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, FileText, Shield, CreditCard } from 'lucide-react';

interface PostSigningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PostSigningModal({ open, onOpenChange }: PostSigningModalProps) {
  const { t } = useTranslation('signinggate');
  const navigate = useNavigate();

  const benefits = [
    { icon: FileText, text: t('benefit_track') },
    { icon: FileText, text: t('benefit_templates') },
    { icon: Shield, text: t('benefit_secure') },
    { icon: CreditCard, text: t('benefit_free') },
  ];

  const handleCreateAccount = () => {
    onOpenChange(false);
    navigate('/join');
  };

  const handleSkip = () => {
    onOpenChange(false);
    navigate('/login');
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      onOpenChange(false);
      navigate('/login');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            {t('post_signing_title')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('post_signing_desc')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 rounded-lg border bg-muted/30 p-4">
          <h4 className="font-semibold text-sm mb-3">{t('create_account_title')}</h4>
          <p className="text-sm text-muted-foreground mb-3">{t('create_account_desc')}</p>
          <ul className="space-y-2">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <benefit.icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span>{benefit.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={handleCreateAccount} className="w-full">
            {t('create_account_button')}
          </Button>
          <Button variant="ghost" onClick={handleSkip} className="w-full text-muted-foreground">
            {t('skip_button')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
